// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import Icon, { MobileOutlined } from '@ant-design/icons';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import { RootState } from '../../../../reducers/reducers';
import { selectBuildSystemIntegration, selectOrgProviders, selectAppSettings } from '../../../../selectors/selectors';
import { AppComponentProps } from '../../../../typescriptHelpers';
import { antBlue, antGrey, runwayColors } from '../../../styles/globalStyles';
import { compact } from 'lodash';
import { getDisplayableWorkflowNames } from '../../../../helpers/displayHelpers';
import { InfoTip } from '../../shared';

const Label: React.FC<{ branch?: string; workflows: string[]; style?: React.CSSProperties }> = ({
    branch,
    workflows,
    style,
}) => (
    <div
        style={{
            display: 'inline-flex',
            gap: 3,
            border: '1px dashed',
            borderColor: antGrey.grey6,
            padding: 3,
            fontSize: 12,
            ...style,
        }}
    >
        {branch && (
            <Text code style={{ padding: '4px 8px', color: antGrey.grey8 }}>
                {branch}
            </Text>
        )}
        {compact(workflows).map((workflow, index) => (
            <div
                key={index}
                style={{
                    padding: '4px 8px',
                    backgroundColor: antGrey.grey7,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Icon component={MobileOutlined} style={{ color: 'white', marginRight: 10 }} />
                <Typography.Text
                    style={{
                        display: 'block',
                        maxWidth: 120,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'left',
                        color: 'white',
                    }}
                >
                    {workflow}
                </Typography.Text>
            </div>
        ))}
    </div>
);

const Commit: React.FC<{ color: string }> = ({ color }) => (
    <div
        style={{
            height: 10,
            width: 10,
            borderRadius: 10,
            border: `4px solid ${runwayColors.white}`,
            backgroundColor: color,
            boxSizing: 'content-box',
            transition: 'all 0.5s ease-in-out',
        }}
    />
);
const Arrow: React.FC<{ color: string; isHalfOnly?: boolean }> = ({ color, isHalfOnly }) => (
    <div
        style={{
            height: 4,
            borderRadius: 4,
            background: `linear-gradient(90deg, ${isHalfOnly ? 'white' : color} 0%, ${
                isHalfOnly ? 'white' : color
            } 50%, ${color} 50%, ${color} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            position: 'relative',
            transition: 'all 0.5s ease-in-out',
        }}
    >
        <div
            style={{
                height: 4,
                backgroundColor: color,
                width: 14,
                borderRadius: 4,
                position: 'absolute',
                transform: 'rotate(-45deg)',
                top: '100%',
                right: -1,
                transition: 'all 0.5s ease-in-out',
            }}
        />
        <div
            style={{
                height: 4,
                backgroundColor: color,
                width: 14,
                borderRadius: 4,
                position: 'absolute',
                transform: 'rotate(45deg)',
                bottom: '100%',
                right: -1,
                transition: 'all 0.5s ease-in-out',
            }}
        />
        <Commit color={isHalfOnly ? 'white' : color} />
        <Commit color={color} />
        <Commit color={color} />
    </div>
);

export const BranchVisualizationInfoViewComponent: React.FC<AppComponentProps> = (props) => {
    const workingBranch = useSelector((state: RootState) => selectAppSettings(state, props.appId).workingBranch);
    const releaseBranchPattern = useSelector(
        // TODO - how can we just take first one here? elsewhere i see us do .find with !ishotfix which seems better but still iffy?
        (state: RootState) => selectAppSettings(state, props.appId).releaseBranchPatterns?.[0],
    );

    const isStaticReleaseBranch = releaseBranchPattern?.isStatic === true;

    const buildSystemIntegration = useSelector((state: RootState) => selectBuildSystemIntegration(state, props.appId));

    const integrationProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));

    const provider = integrationProviders?.find((provider) => provider.id === buildSystemIntegration?.providerId);

    // get displayable names
    const { workingBranchWorkflow, RCWorkflow, productionWorkflow } = getDisplayableWorkflowNames({
        buildSystemIntegration,
        provider,
        shortString: true,
    });

    const hasDifferentReleaseBranch = workingBranch !== releaseBranchPattern?.pattern;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {releaseBranchPattern && (
                <Label
                    style={{ alignSelf: 'end', marginBottom: 23 }}
                    branch={releaseBranchPattern?.pattern}
                    workflows={[RCWorkflow, productionWorkflow]}
                />
            )}
            <div
                style={{
                    display: 'flex',
                    gap: 70,
                    flexDirection: 'column',
                    backgroundColor: runwayColors.white,
                    position: 'relative',
                }}
            >
                {releaseBranchPattern && (
                    <>
                        {hasDifferentReleaseBranch && (
                            <svg
                                style={{
                                    width: '28%',
                                    height: '100%',
                                    left: '23%',
                                    position: 'absolute',
                                }}
                            >
                                <line x1="0" y1="100%" x2="100%" y2="0" stroke={antGrey.grey5} strokeWidth="4" />
                            </svg>
                        )}

                        <Arrow color={runwayColors.statusGreen} isHalfOnly={!isStaticReleaseBranch} />
                    </>
                )}

                {hasDifferentReleaseBranch && workingBranch && <Arrow color={antBlue.blue5} />}
            </div>
            {workingBranch && (
                <Label
                    style={{ alignSelf: 'start', marginTop: 23 }}
                    branch={workingBranch}
                    workflows={[workingBranchWorkflow]}
                />
            )}
            <br />
            <InfoTip>
                <span>
                    Read more about tokenized patterns in our{' '}
                    <Typography.Link
                        href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                        target="_blank"
                    >
                        docs.
                    </Typography.Link>
                </span>
            </InfoTip>
        </div>
    );
};
